import { matchesFileAccept } from "./uploadAccept.ts";
import { mergeFiles } from "../components/upload/fileSelection.ts";

export type DraftMode = "scan" | "checkup";
export type DraftProject = { id: string; name: string };
export type DraftSubmission = {
  ownerId: string; revision: number; mode: DraftMode;
  files: File[]; reference: File[]; statsText: string; referenceText: string;
  projectId: string | null;
};
export type DraftSnapshot = {
  ownerId: string; revision: number; files: File[]; reference: File[];
  statsText: string; referenceText: string;
  project: DraftProject | null;
  duplicates: { primary: number; reference: number };
  overflow: { primary: number; reference: number };
};
export const imageEligible = (file: File) => matchesFileAccept(file, "image/*,.pdf");

/** One memory-only manuscript draft. Submitted snapshots never own later edits. */
export function createWorkspaceDraftStore(ownerId: string, limit = 200) {
  let state: DraftSnapshot = { ownerId, revision: 0, files: [], reference: [], statsText: "", referenceText: "", project: null,
    duplicates: { primary: 0, reference: 0 }, overflow: { primary: 0, reference: 0 } };
  const listeners = new Set<() => void>();
  const update = (patch: Partial<DraftSnapshot>) => {
    state = { ...state, ...patch, revision: state.revision + 1 };
    listeners.forEach(listener => listener());
  };
  const filesFor = (role: "primary" | "reference") => role === "primary" ? state.files : state.reference;
  const counts = (role: "primary" | "reference", duplicates = 0, overflow = 0) => ({
    duplicates: { ...state.duplicates, [role]: duplicates }, overflow: { ...state.overflow, [role]: overflow },
  });
  const setFiles = (role: "primary" | "reference", files: File[], duplicates = 0, overflow = 0) => update({
    [role === "primary" ? "files" : "reference"]: files, ...counts(role, duplicates, overflow),
  });
  return {
    getSnapshot: () => state,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    add(role: "primary" | "reference", incoming: File[]) {
      const other = filesFor(role === "primary" ? "reference" : "primary").length;
      const [next, duplicates, overflow] = mergeFiles(filesFor(role), incoming, Math.max(0, limit - other));
      setFiles(role, next, duplicates, overflow);
    },
    remove(role: "primary" | "reference", file: File) { setFiles(role, filesFor(role).filter(value => value !== file)); },
    replace(role: "primary" | "reference", files: File[]) {
      const other = filesFor(role === "primary" ? "reference" : "primary").length;
      const [next, duplicates, overflow] = mergeFiles([], files, Math.max(0, limit - other));
      setFiles(role, next, duplicates, overflow);
    },
    clearFiles(role: "primary" | "reference", eligibleOnly = false) {
      setFiles(role, eligibleOnly ? filesFor(role).filter(file => !imageEligible(file)) : []);
    },
    setStatsText(statsText: string) { update({ statsText }); },
    setReferenceText(referenceText: string) { update({ referenceText }); },
    setProject(project: DraftProject) { update({ project: { ...project } }); },
    clearProject() { update({ project: null }); },
    capture(mode: DraftMode): DraftSubmission {
      return { ownerId, revision: state.revision, mode,
        files: mode === "scan" ? state.files.filter(imageEligible) : [...state.files],
        reference: mode === "checkup" ? [...state.reference] : [],
        statsText: mode === "checkup" ? state.statsText : "",
        referenceText: mode === "checkup" ? state.referenceText : "",
        projectId: state.project?.id || null };
    },
    complete(submitted: DraftSubmission) {
      if (submitted.ownerId !== ownerId) return;
      const main = new Set(submitted.files), reference = new Set(submitted.reference);
      update({ files: state.files.filter(file => !main.has(file)), reference: state.reference.filter(file => !reference.has(file)),
        ...(submitted.mode === "checkup" && state.statsText === submitted.statsText ? { statsText: "" } : {}),
        ...(submitted.mode === "checkup" && state.referenceText === submitted.referenceText ? { referenceText: "" } : {}),
        duplicates: { primary: 0, reference: 0 }, overflow: { primary: 0, reference: 0 } });
    },
    clear() { update({ files: [], reference: [], statsText: "", referenceText: "",
      duplicates: { primary: 0, reference: 0 }, overflow: { primary: 0, reference: 0 } }); },
  };
}
export type WorkspaceDraftStore = ReturnType<typeof createWorkspaceDraftStore>;
